import { matchPath } from 'react-router-dom'

import { DEFAULT_TERRITORY_DATE } from 'modules/territory/common/territory.consts'
import { FeatureFlagsStore } from 'stores'
import { getTerritoryUrlByTerritoryId } from 'utils/getTerritoryUrlByTerritoryId'
import { jsonUtils } from 'utils/jsonUtils'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'
import { getTimeRange } from 'utils/timeRange'

import { PATHS_BY_ENTITY_TYPE, TERRITORY_ENTITIES } from './consts'
import type { Audience, TerritoryEntityType } from './types'

/**
 * Format audience to be used in the charts since backend returns an array of objects
 * @param {array} ageGender - ageGender as it comes from audience query
 * @returns {object} Audience formatted to be used in the charts
 */
function getAudienceByGender(ageGender = []) {
  const audience: Audience = {}

  ageGender.forEach(maleOrFemaleAudienceByAge => {
    const { gender: _gender, __typename, ...rest } = maleOrFemaleAudienceByAge
    if (maleOrFemaleAudienceByAge.gender === 'f') {
      audience.female = rest
    }
    if (maleOrFemaleAudienceByAge.gender === 'm') {
      audience.male = rest
    }
  })

  audience.femaleAudience = Object.values(audience?.female || {})
  audience.maleAudience = Object.values(audience?.male || {})
  audience.femaleAudienceTotal = audience.femaleAudience.reduce((a, b) => a + b, 0) * 100
  audience.maleAudienceTotal = audience.maleAudience.reduce((a, b) => a + b, 0) * 100

  return audience
}

export { getAudienceByGender }

export function getFormatedDateVariablesForTerritory(
  value,
  defaultTimeRange = DEFAULT_TERRITORY_DATE.dateValueAsString
) {
  if (!value && defaultTimeRange) {
    return getFormatedDateVariablesForTerritory(defaultTimeRange)
  }
  if (value === '7' || value === '0') {
    return getTimeRange('day', 7)
  }
  if (value === '1') {
    return getTimeRange('day', 30)
  }
  if (value === '3') {
    return getTimeRange('month', 3)
  }
  if (value === '6') {
    return getTimeRange('month', 6)
  }
  if (value === '12') {
    return getTimeRange('month', 12)
  }
  if (value === '24') {
    return getTimeRange('month', 24)
  }
  if (value === '36') {
    return getTimeRange('month', 36)
  }
  if (value === '61') {
    return getTimeRange('month', 61)
  }
  if (value === '24h' || value === 'today') {
    return getTimeRange('hour', 24)
  }
  return null
}

export function getTerritoryLinkByType(type, territoryId, workspaceId, scopeCategoryId = null) {
  // Inside a Promoted Niche (scoped route), same-page niche links must keep the
  // `category/:categoryId` segment so navigation stays within the scope. See
  // PATHNAMES.WORKSPACE_CATEGORY_NICHE.
  if (scopeCategoryId && type === 'niche') {
    return getRoute(PATHNAMES.WORKSPACE_CATEGORY_NICHE, {
      panelId: workspaceId,
      categoryId: scopeCategoryId,
      entityId: territoryId,
    })
  }

  const hasAccessToIndustryCategory = FeatureFlagsStore.isFeatureFlagEnabled('hasAccessToIndustryCategory')
  const isNewPageBrandOverviewEnabled = FeatureFlagsStore.isFeatureFlagEnabled('isNewPageBrandOverviewEnabled')
  const territoriesLinkObjMap = {
    topic: PATHNAMES.WORKSPACE_TOPIC,
    subtopic: PATHNAMES.WORKSPACE_SUBTOPIC,
    brand_territory: isNewPageBrandOverviewEnabled ? PATHNAMES.WORKSPACE_BRAND_OVERVIEW : PATHNAMES.WORKSPACE_BRAND,
    brand_group: PATHNAMES.WORKSPACE_BRAND_GROUP,
    industry_topic: hasAccessToIndustryCategory ? PATHNAMES.INDUSTRY_CATEGORY : PATHNAMES.WORKSPACE_TOPIC,
    global_topic: PATHNAMES.WORKSPACE_GLOBAL_TOPIC,
    trending_subject: PATHNAMES.WORKSPACE_TRENDING_TAG,
    niche: PATHNAMES.WORKSPACE_NICHE,
    trending: PATHNAMES.WORKSPACE_TRENDING,
    tag: PATHNAMES.WORKSPACE_TAG,
    default: PATHNAMES.WORKSPACE_SAVED_SEARCH,
    hot_topic: PATHNAMES.WORKSPACE_HOT_TOPIC,
  }

  const link = getRoute(territoriesLinkObjMap[type] || territoriesLinkObjMap.default, {
    panelId: workspaceId,
    entityId: type === 'trending_subject' ? `trending_subject.${territoryId}` : territoryId,
  })

  return link
}

const getBaseUrl = () => {
  const pathname = window.location.pathname
  const splitPath = pathname.split('/')
  splitPath.pop()
  const basePath = splitPath.join('/')

  return basePath
}

export function getNewTabLink(tab) {
  const tabLinkSearchParams = new URLSearchParams(window.location.search)
  const baseUrl = getBaseUrl()
  return `${baseUrl}/${tab}?${tabLinkSearchParams.toString()}`
}

export function getTerritoryIdByType(id, type) {
  if (type === 'SavedPostSearch') {
    return `saved_search.${id}`
  }

  if ('globalTopic' === type) {
    return `global_topic.${id}`
  }

  return `${type}.${id}`
}

export function getEntityNameByLocation(pathname: string): TerritoryEntityType | null {
  for (const [entityType, paths] of Object.entries(PATHS_BY_ENTITY_TYPE)) {
    if (matchPath(pathname, { path: paths })) {
      return TERRITORY_ENTITIES[entityType]
    }
  }

  return null
}

interface GetTerritoryRouteWithFiltersObjectProps {
  /** The id of the territory */
  territoryId: string
  /** A stringfied object with the `publishedAt` field */
  filtersObject: string
  /** The workspace id */
  workspaceId: string
}

function getTerritoryRouteWithFiltersObject({
  territoryId,
  filtersObject = '{}',
  workspaceId,
}: GetTerritoryRouteWithFiltersObjectProps) {
  const filtersObjectParsed = jsonUtils.parse(filtersObject)
  const searchParams = new URLSearchParams()

  if (filtersObjectParsed?.publishedAt) {
    const { gte, lt } = filtersObjectParsed.publishedAt
    const uploadDate = `custom_${gte}_${lt}`
    searchParams.append('uploadDate', uploadDate)
  }

  const tab = 'overview'
  const path = getTerritoryUrlByTerritoryId(territoryId, workspaceId)
  const pathWithTab = `${path}/${tab}`
  const completePath = `${pathWithTab}?${searchParams.toString()}`

  return completePath
}

export default {
  getAudienceByGender,
  getFormatedDateVariablesForTerritory,
  getTerritoryLinkByType,
  getNewTabLink,
  getTerritoryIdByType,
  getEntityNameByLocation,
  getTerritoryRouteWithFiltersObject,
}
